/**
 * Boot-time Okta configuration validation. Runs before `new OktaAuth(...)`
 * so a missing or malformed env var fails loud at startup — with an
 * actionable message — instead of producing a cryptic Okta SDK error on
 * first login attempt.
 *
 * Typical failure modes this catches:
 *   - `.env` not loaded locally (`OKTA_CLIENT_ID` undefined)
 *   - AWS Secrets Manager lookup partially failed in Amplify build
 *   - Issuer missing scheme (`sevaro.okta.com` instead of `https://...`)
 *   - Stray whitespace from copy/paste into Secrets Manager
 */
interface OktaOidcShape {
  clientId?: string;
  issuer?: string;
}

export function assertOktaConfig(oidc: OktaOidcShape): void {
  const errors: string[] = [];

  const clientId = oidc.clientId?.trim();
  if (!clientId || clientId === "undefined" || clientId === "null") {
    errors.push(
      "OKTA_CLIENT_ID is not configured. Check .env (local) or AWS Secrets Manager (Amplify).",
    );
  } else if (/\s/.test(oidc.clientId ?? "")) {
    errors.push(
      "OKTA_CLIENT_ID contains whitespace — strip leading/trailing spaces.",
    );
  }

  const issuer = oidc.issuer?.trim();
  if (!issuer || issuer === "undefined" || issuer === "null") {
    errors.push(
      "OKTA_ISSUER is not configured. Check .env (local) or AWS Secrets Manager (Amplify).",
    );
  } else {
    if (!/^https?:\/\//.test(issuer)) {
      errors.push(
        `OKTA_ISSUER must start with https:// (got "${issuer}"). Include the scheme.`,
      );
    }
    if (/\s/.test(oidc.issuer ?? "")) {
      errors.push("OKTA_ISSUER contains whitespace — strip leading/trailing spaces.");
    }
  }

  if (errors.length > 0) {
    throw new Error(
      `Okta configuration invalid:\n  - ${errors.join("\n  - ")}`,
    );
  }
}
